// C10 — Importar matriz de capacidades desde Excel (SKU × Línea).
//
// Cada fila del Excel = una celda de la matriz: capacidad de un SKU en una línea.
// La plantilla trae TODOS los campos de la celda (capacidad, personas, golpes)
// porque el upsert los reemplaza: importar solo "capacidad" pisaría la config
// existente. Filas incompletas se marcan como error y NO se cargan.
//
// Parseo + resolución de códigos en el FE (SheetJS); el backend revalida scope
// y existencia, y aplica todo de forma atómica (POST .../skus/lineas/bulk).

import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import type { CoordinadorSkusMatriz, SkuLineaBulkItem } from 'schemas';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useImportarPreciosBulk, useImportarSkusLineasBulk } from '../../lib/api/queries';
import { ApiError } from '../../lib/api/client';
import {
  filasExportCapacidades,
  headerCapacidades,
  norm,
  parsearFilasCapacidades,
  type CeldaExcel,
  type FilaParseada,
} from './capacidadesExcel';

type Data = CoordinadorSkusMatriz;

const hoyISO = () => new Date().toISOString().slice(0, 10);

export function C10ImportarExcelDialog({
  data,
  puedeVerCostos,
  onClose,
}: {
  data: Data;
  puedeVerCostos: boolean;
  onClose: () => void;
}) {
  const [filas, setFilas] = useState<FilaParseada[]>([]);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [vigenciaPrecios, setVigenciaPrecios] = useState(hoyISO());
  const [erroresBackend, setErroresBackend] = useState<
    Array<{ sku_id: string; linea_id: string; mensaje: string }>
  >([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const importar = useImportarSkusLineasBulk();
  const importarPrecios = useImportarPreciosBulk();

  // Resolución: SKU por código_cliente; Línea por código O por nombre (ambos del scope).
  const skuPorCodigo = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of data.filas) m.set(norm(f.codigo_cliente), f.sku_id);
    return m;
  }, [data.filas]);
  const skuDesc = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of data.filas) m.set(f.sku_id, f.descripcion);
    return m;
  }, [data.filas]);
  const lineaPorTexto = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of data.lineas) {
      m.set(norm(l.codigo), l.id);
      m.set(norm(l.nombre), l.id);
    }
    return m;
  }, [data.lineas]);
  const lineaLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of data.lineas) m.set(l.id, l.nombre);
    return m;
  }, [data.lineas]);

  /**
   * Arma el libro con la hoja "Capacidades" (+ hojas de referencia) a partir de
   * las filas de datos ya formateadas. Lo comparten la plantilla vacía y el
   * export de lo configurado: MISMA forma, para que el archivo exportado se
   * pueda editar y volver a subir sin tocar nada.
   */
  function construirLibro(filasDatos: CeldaExcel[][]) {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headerCapacidades(puedeVerCostos), ...filasDatos]);
    XLSX.utils.book_append_sheet(wb, ws, 'Capacidades');
    // Hoja referencia: líneas del scope.
    const wsLineas = XLSX.utils.aoa_to_sheet([
      ['LÍNEAS DE TU SCOPE', ''],
      ['Código', 'Nombre'],
      ...data.lineas.map((l) => [l.codigo, l.nombre]),
    ]);
    XLSX.utils.book_append_sheet(wb, wsLineas, 'Lineas');
    // Hoja referencia: SKUs del tenant.
    const wsSkus = XLSX.utils.aoa_to_sheet([
      ['SKUS DEL CATÁLOGO', ''],
      ['Código', 'Descripción'],
      ...data.filas.map((f) => [f.codigo_cliente, f.descripcion]),
    ]);
    XLSX.utils.book_append_sheet(wb, wsSkus, 'SKUs');
    return wb;
  }

  function descargarPlantilla() {
    const ejemplo = [
      data.filas[0]?.codigo_cliente ?? '10148',
      data.lineas[0]?.nombre ?? 'ATLANTA 1',
      2375,
      4,
      'NO',
      '',
      '',
      ...(puedeVerCostos ? [370] : []),
    ];
    XLSX.writeFile(construirLibro([ejemplo]), `capacidades-${data.tenant.nombre}.xlsx`);
  }

  /**
   * Exporta la configuración ACTUAL (una fila por celda SKU×línea configurada),
   * con TODOS los campos. Es el mismo formato que consume el import: se edita la
   * columna que haga falta y se vuelve a subir. Solo salen celdas configuradas —
   * una fila sin capacidad sería rechazada por el import de todas formas; para
   * dar de alta una celda nueva se agrega la fila usando las hojas de referencia.
   */
  function exportarActual() {
    const filasDatos = filasExportCapacidades(data, puedeVerCostos);
    if (filasDatos.length === 0) {
      toast.error('No hay capacidades configuradas para exportar');
      return;
    }
    XLSX.writeFile(
      construirLibro(filasDatos),
      `capacidades-actual-${data.tenant.nombre}-${hoyISO()}.xlsx`,
    );
    toast.success(`${filasDatos.length} capacidad(es) exportada(s)`);
  }

  function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setNombreArchivo(file.name);
    setErroresBackend([]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'array' });
        const ws = wb.Sheets['Capacidades'] ?? wb.Sheets[wb.SheetNames[0]!];
        const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws!, { header: 1, raw: false, defval: '' });
        const out = parsearFilasCapacidades(aoa, {
          skuPorCodigo,
          lineaPorTexto,
          puedeVerCostos,
        });
        setFilas(out);
        if (out.length === 0) toast.error('El archivo no tiene filas de datos');
      } catch {
        toast.error('No se pudo leer el archivo. ¿Es un .xlsx válido?');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  const errores = filas.filter((f) => f.error);
  const cargables = filas.filter((f) => !f.error);
  const puedeCargar = errores.length === 0 && cargables.length > 0;

  async function cargar() {
    if (!puedeCargar) return;
    const items: SkuLineaBulkItem[] = cargables.map((f) => ({
      sku_id: f.sku_id!,
      linea_id: f.linea_id!,
      capacidad_estandar_unidades_hora: f.capacidad!,
      personas_estandar_turno: f.personas!,
      golpes_min_aplica: f.golpesAplica,
      capacidad_estandar_golpes_min: f.golpesAplica ? f.capGolpes : null,
      bache_minimo: f.bache,
    }));
    try {
      const res = await importar.mutateAsync({ items });
      if (res.errores.length > 0) {
        setErroresBackend(res.errores);
        toast.error(`El servidor rechazó ${res.errores.length} fila(s). No se aplicó nada.`);
        return;
      }
      // Precios por SKU (deduplicados): un precio por SKU, vigencia única del lote.
      let preciosMsg = '';
      if (puedeVerCostos) {
        const precioPorSku = new Map<string, number>();
        for (const f of cargables) {
          if (f.sku_id && f.precio !== null) precioPorSku.set(f.sku_id, f.precio);
        }
        if (precioPorSku.size > 0) {
          const precioItems = [...precioPorSku].map(([sku_id, precio_unitario_cop]) => ({
            sku_id,
            precio_unitario_cop,
          }));
          const pr = await importarPrecios.mutateAsync({
            vigente_desde: vigenciaPrecios,
            items: precioItems,
          });
          if (pr.errores.length > 0) {
            toast.error(`Capacidades OK, pero ${pr.errores.length} precio(s) fallaron`, {
              description: pr.errores
                .slice(0, 3)
                .map((e) => e.mensaje)
                .join(' · '),
            });
            return;
          }
          preciosMsg = ` · ${pr.procesadas} precio(s)`;
        }
      }
      toast.success(`${res.procesadas} capacidad(es) importada(s)${preciosMsg}`);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error al importar';
      toast.error('No se pudo importar', { description: msg });
    }
  }

  return (
    <Dialog open onClose={onClose} className="max-w-4xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Importar / exportar capacidades en Excel</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <Button
              variant="outline"
              type="button"
              onClick={exportarActual}
              title="Descarga las capacidades ya configuradas, en el mismo formato del import"
            >
              Exportar datos actuales
            </Button>
            <Button variant="outline" type="button" onClick={descargarPlantilla}>
              Descargar plantilla vacía
            </Button>
            <Button variant="outline" type="button" onClick={() => inputRef.current?.click()}>
              Subir archivo
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={onArchivo}
            />
            {nombreArchivo ? (
              <span className="text-xs text-fg-tertiary">{nombreArchivo}</span>
            ) : null}
          </div>

          {filas.length > 0 ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm">
                {cargables.length} lista(s)
                {errores.length > 0 ? (
                  <span className="text-crit"> · {errores.length} con error</span>
                ) : null}
              </p>
              {puedeVerCostos && cargables.some((f) => f.precio !== null) ? (
                <label className="flex items-center gap-2 text-xs text-fg-tertiary">
                  Vigencia de los precios:
                  <input
                    type="date"
                    value={vigenciaPrecios}
                    onChange={(e) => setVigenciaPrecios(e.target.value)}
                    className="rounded border border-border bg-surface-base px-2 py-1 text-xs text-fg"
                  />
                </label>
              ) : null}
              <div className="max-h-80 overflow-auto rounded border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-surface-overlay text-fg-tertiary">
                    <tr>
                      <th className="px-2 py-1 text-left">Fila</th>
                      <th className="px-2 py-1 text-left">SKU</th>
                      <th className="px-2 py-1 text-left">Línea</th>
                      <th className="px-2 py-1 text-right">Cap. u/h</th>
                      <th className="px-2 py-1 text-right">Pers.</th>
                      {puedeVerCostos && <th className="px-2 py-1 text-right">Precio</th>}
                      <th className="px-2 py-1 text-left">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((f) => (
                      <tr key={f.idx} className="border-t border-border">
                        <td className="px-2 py-1 align-top text-fg-tertiary">{f.filaExcel}</td>
                        <td className="px-2 py-1 align-top">
                          <div className="font-medium">{f.skuTexto}</div>
                          {f.sku_id && skuDesc.get(f.sku_id) ? (
                            <div className="text-fg-tertiary">{skuDesc.get(f.sku_id)}</div>
                          ) : null}
                        </td>
                        <td className="px-2 py-1 align-top">
                          {f.linea_id ? (lineaLabel.get(f.linea_id) ?? f.lineaTexto) : f.lineaTexto}
                        </td>
                        <td className="px-2 py-1 text-right align-top tabular-nums">
                          {f.capacidad ?? '—'}
                        </td>
                        <td className="px-2 py-1 text-right align-top tabular-nums">
                          {f.personas ?? '—'}
                        </td>
                        {puedeVerCostos && (
                          <td className="px-2 py-1 text-right align-top tabular-nums">
                            {f.precio !== null ? `$${f.precio.toLocaleString('es-CO')}` : '—'}
                          </td>
                        )}
                        <td className="px-2 py-1 align-top">
                          {f.error ? (
                            <span className="text-crit">{f.error}</span>
                          ) : (
                            <span className="text-ok">Lista</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {errores.length > 0 ? (
                <p className="text-xs text-crit">
                  Corrija las filas con error en el Excel y vuelva a subirlo. No se carga nada hasta
                  que todas las filas estén listas.
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-fg-tertiary">
              Para actualizar capacidades ya cargadas, use <strong>Exportar datos actuales</strong>:
              baja un archivo con una fila por cada celda SKU×línea configurada, edite lo que
              necesite y vuelva a subirlo. Si va a configurar celdas nuevas, descargue la plantilla
              vacía (SKU · Línea · Capacidad u/h · Personas · Golpes/min · Bache). Las hojas
              “Lineas” y “SKUs” traen los códigos válidos de su scope. Cada fila reemplaza la
              configuración completa de esa celda, así que no borre columnas.
              {puedeVerCostos
                ? ' La columna “Precio u (COP)” es opcional: ponga el mismo precio en todas las filas del SKU (se aplica una vez por SKU, con la fecha de vigencia que usted elija).'
                : ''}
            </p>
          )}

          {erroresBackend.length > 0 ? (
            <div className="rounded border border-crit/40 bg-crit/5 p-2 text-xs text-crit">
              <p className="font-medium">El servidor rechazó estas filas (no se aplicó nada):</p>
              <ul className="mt-1 list-disc pl-4">
                {erroresBackend.slice(0, 10).map((e, i) => (
                  <li key={i}>{e.mensaje}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={cargar} disabled={!puedeCargar || importar.isPending}>
            {importar.isPending ? 'Importando…' : `Importar ${cargables.length} fila(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
